import { forwardRef, type ReactNode } from 'react';
import {
	FormControl,
	FormLabel,
	FormHelperText,
	FormErrorMessage,
	Input,
	Textarea,
	type InputProps,
	type TextareaProps,
} from '@chakra-ui/react';

/**
 * Shared field-wrapper contract: label + helper + error in one place so every
 * form field looks and behaves the same. `TextField` wraps Input, `TextArea`
 * wraps Textarea.
 */
interface FieldShellProps {
	label?: ReactNode;
	helperText?: ReactNode;
	error?: ReactNode;
	isRequired?: boolean;
	isInvalid?: boolean;
	isDisabled?: boolean;
}

const FieldShell = ({
	label,
	helperText,
	error,
	isRequired,
	isInvalid,
	isDisabled,
	children,
}: FieldShellProps & { children: ReactNode }) => (
	<FormControl
		isRequired={isRequired}
		isInvalid={isInvalid ?? Boolean(error)}
		isDisabled={isDisabled}
	>
		{label ? (
			<FormLabel fontSize="sm" fontWeight="semibold" color="fg.default" mb={1}>
				{label}
			</FormLabel>
		) : null}
		{children}
		{error ? (
			<FormErrorMessage>{error}</FormErrorMessage>
		) : helperText ? (
			<FormHelperText color="fg.muted">{helperText}</FormHelperText>
		) : null}
	</FormControl>
);

export interface TextFieldProps extends InputProps, FieldShellProps {}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
	({ label, helperText, error, isRequired, isInvalid, isDisabled, ...inputProps }, ref) => (
		<FieldShell
			label={label}
			helperText={helperText}
			error={error}
			isRequired={isRequired}
			isInvalid={isInvalid}
			isDisabled={isDisabled}
		>
			<Input ref={ref} {...inputProps} />
		</FieldShell>
	),
);
TextField.displayName = 'TextField';

export interface TextAreaProps extends TextareaProps, FieldShellProps {}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	({ label, helperText, error, isRequired, isInvalid, isDisabled, ...areaProps }, ref) => (
		<FieldShell
			label={label}
			helperText={helperText}
			error={error}
			isRequired={isRequired}
			isInvalid={isInvalid}
			isDisabled={isDisabled}
		>
			<Textarea ref={ref} {...areaProps} />
		</FieldShell>
	),
);
TextArea.displayName = 'TextArea';

export default TextField;
